import {
    GridViewRounded,
    InfoRounded,
    NotificationsNoneOutlined,
    WarningAmberRounded,
} from "@mui/icons-material";
import {
    Alert,
    AlertTitle,
    Box,
    Checkbox,
    LinearProgress,
    Skeleton,
    Stack,
    SxProps,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Typography,
} from "@mui/material";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxToggleButton from "components/UI/AcxToggleButton";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import React, { useContext, useEffect, useState } from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import type Question from "models/Question";
import {
    getCallsPerLabelRows,
    getHighUpsamplingRows,
    getPerOutcomeRows,
    getTokenEstimateText,
    SyntheticDataRow,
} from "./syntheticDataRows";
import { planSyntheticDataBatches } from "./syntheticDataBatches";
import {
    getBatchTotals,
    getFailureHeadline,
    getLastCompletedOn,
    getSavedCallsByResult,
    getSavedCallTotal,
    getTimeLeftText,
    hasGeneratingBatches,
    progressPercent,
} from "./syntheticDataBatchProgress";
import { parseFromISO } from "utils/DateTimeUtils";
import type { SyntheticDataBatch } from "services/SyntheticDataService";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { v4 } from "uuid";

// Opens the Generate dialog, which lives on the section so it stays open when the view under it changes.
const OpenGenerateDialogContext = React.createContext<() => void>(
    () => undefined,
);

const callsPerLabelOptions = Array.from({ length: 10 }, (_, index) => ({
    value: index + 1,
    label: `${index + 1}×`,
}));

export const SyntheticDataSection = observer(() => {
    const store = useStore(LLMClassifierBuilderStore);
    const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);

    useEffect(() => store.keepDataStepLabelCountsCurrent(), [store]);

    const hasLabeledData = !!store.dataLabelingQuestion;
    const batches = store.syntheticDataBatches;
    const failureHeadline = batches && getFailureHeadline(batches);

    let chip: React.ReactNode;
    let content: React.ReactNode;
    if (batches && hasGeneratingBatches(batches)) {
        chip = <AcxChip size="small" color="lightBlue" label="Generating" />;
        content = <SyntheticDataProgress batches={batches} />;
    } else if (failureHeadline) {
        chip = <AcxChip size="small" color="red" label="Failed" />;
        content = (
            <SyntheticDataFailed
                headline={failureHeadline}
                canGenerate={hasLabeledData}
            />
        );
    } else if (batches?.length) {
        chip = <AcxChip size="small" color="green" label="Ready" />;
        content = (
            <SyntheticDataReady
                batches={batches}
                canGenerate={hasLabeledData}
            />
        );
    } else if (!hasLabeledData) {
        content = <NeedsLabeledData />;
    } else if (store.isSaved && !batches) {
        content = <Skeleton variant="rounded" height={88} />;
    } else {
        content = <NoSyntheticSetYet />;
    }

    return (
        <OpenGenerateDialogContext.Provider
            value={() => setIsGenerateDialogOpen(true)}
        >
            <TitledSection
                title="Synthetic data"
                subtitle="Generated from your human labels and saved on this model."
                variant="bordered"
                action={chip}
            >
                {content}
            </TitledSection>
            {isGenerateDialogOpen && (
                <GenerateSyntheticDataDialog
                    onClose={() => setIsGenerateDialogOpen(false)}
                />
            )}
        </OpenGenerateDialogContext.Provider>
    );
});

/** Shows calls written across all of the model's batches while any is still generating. */
function SyntheticDataProgress(props: { batches: SyntheticDataBatch[] }) {
    const totals = getBatchTotals(props.batches);

    return (
        <Stack rowGap={2} width="100%">
            <Stack direction="row" alignItems="baseline" columnGap={1}>
                <Typography
                    fontSize="28px"
                    fontWeight={600}
                    lineHeight={1}
                    sx={(theme) => ({ color: theme.palette.blue.main })}
                >
                    {totals.generatedCount.toLocaleString()}
                </Typography>
                <Typography color="text.secondary">
                    of {totals.requestedCount.toLocaleString()} synthetic{" "}
                    {totals.requestedCount === 1 ? "call" : "calls"} written
                </Typography>
                <Typography color="text.secondary" marginLeft="auto">
                    {getTimeLeftText(
                        totals.generatedCount,
                        totals.requestedCount,
                    )}
                </Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={progressPercent(
                    totals.generatedCount,
                    totals.requestedCount,
                )}
                sx={(theme) => ({
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: theme.palette.lightgrayBorder.main,
                    "& .MuiLinearProgress-bar": { borderRadius: 5 },
                })}
            />
            <Stack
                direction="row"
                alignItems="center"
                columnGap={1.5}
                sx={noteSx}
            >
                <NotificationsNoneOutlined
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                />
                <Typography variant="body2" color="text.secondary">
                    Runs in the background. You’re notified when the set is
                    ready. You can keep testing with human labels in the
                    meantime.
                </Typography>
            </Stack>
        </Stack>
    );
}

function getHumanLabelTotal(store: LLMClassifierBuilderStore) {
    return (store.dataStepLabelCounts ?? []).reduce(
        (sum, count) => sum + count.count,
        0,
    );
}

const GenerateSyntheticDataButton = observer(
    (props: { label: string; color?: "primary" | "error" }) => {
        const store = useStore(LLMClassifierBuilderStore);
        const openGenerateDialog = useContext(OpenGenerateDialogContext);
        const status = store.dataStepLabelCountsStatus;
        const hasNoLabels =
            status === "loaded" && getHumanLabelTotal(store) === 0;

        return (
            <AcxButton
                variant="outlined"
                color={props.color ?? "primary"}
                disabled={status !== "loaded" || hasNoLabels}
                onClick={openGenerateDialog}
            >
                {props.label}
            </AcxButton>
        );
    },
);

function SyntheticDataFailed(props: {
    headline: string;
    canGenerate: boolean;
}) {
    return (
        <Stack rowGap={2} width="100%">
            <Alert
                severity="error"
                icon={<WarningAmberRounded />}
                sx={(theme) => ({
                    color: theme.palette.error.dark,
                    backgroundColor: theme.palette.error[50],
                    border: `1px solid ${theme.palette.error[200]}`,
                    borderRadius: "8px",
                    "& .MuiAlert-icon": { color: theme.palette.error.dark },
                })}
            >
                <AlertTitle sx={{ fontWeight: 600, color: "inherit" }}>
                    {props.headline}
                </AlertTitle>
                Nothing was saved and your human labels are unchanged.
            </Alert>
            {props.canGenerate && (
                <Box>
                    <GenerateSyntheticDataButton label="Generate again" />
                </Box>
            )}
        </Stack>
    );
}

/** The question text sent with each batch as its label name. */
function getLabelName(question: Question) {
    return question.questionText?.trim() || question.shortQuestionText || "";
}

/** The finished set: calls saved across the model's succeeded batches. */
const SyntheticDataReady = observer(
    (props: { batches: SyntheticDataBatch[]; canGenerate: boolean }) => {
        const store = useStore(LLMClassifierBuilderStore);
        const results = getSavedCallsByResult(props.batches);
        const total = getSavedCallTotal(props.batches);
        const completedOn = getLastCompletedOn(props.batches);

        // The model's synthetic calls are always seeded from its linked module.
        const seedModule = store.dataLabelingModule;
        const humanLabelsByResult = new Map(
            getCallsPerLabelRows(store.dataStepLabelCounts ?? [], 0).map(
                (row) => [row.result, row.humanLabels],
            ),
        );

        function humanLabelsFor(result: string): React.ReactNode {
            if (!store.dataLabelingQuestion) return "—";
            if (store.dataStepLabelCountsStatus === "loading")
                return <Skeleton variant="text" width="2.5em" />;
            if (store.dataStepLabelCountsStatus === "error") return "—";
            return (humanLabelsByResult.get(result) ?? 0).toLocaleString();
        }

        return (
            <Stack rowGap={2} width="100%">
                <Stack
                    direction="row"
                    sx={(theme) => ({
                        border: `1px solid ${theme.palette.lightgrayBorder.main}`,
                        borderRadius: "8px",
                        "& > *:not(:last-child)": {
                            borderRight: `1px solid ${theme.palette.lightgrayBorder.main}`,
                        },
                    })}
                >
                    <SummaryCell label="Synthetic calls">
                        <Typography
                            fontSize="28px"
                            fontWeight={600}
                            lineHeight={1.1}
                        >
                            {total.toLocaleString()}
                        </Typography>
                    </SummaryCell>
                    <SummaryCell label="Seeded from">
                        <Typography fontWeight={600}>
                            {seedModule?.name ?? "—"}
                        </Typography>
                    </SummaryCell>
                    <SummaryCell label="Generated">
                        <Typography fontWeight={600}>
                            {completedOn &&
                                `${parseFromISO(completedOn).format("L")} · `}
                        </Typography>
                    </SummaryCell>
                </Stack>
                {results.length > 0 && (
                    <TableContainer sx={tableContainerSx}>
                        <Table
                            sx={{
                                "& .MuiTableCell-root": {
                                    padding: "11px 16px",
                                },
                            }}
                        >
                            <TableHead sx={tableHeadSx}>
                                <TableRow>
                                    <TableCell>Result</TableCell>
                                    <TableCell>Human labels</TableCell>
                                    <TableCell>Synthetic calls</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    "& tr:last-child td": { borderBottom: 0 },
                                }}
                            >
                                {results.map((result) => (
                                    <TableRow key={result.result}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {result.result}
                                        </TableCell>
                                        <TableCell>
                                            {humanLabelsFor(result.result)}
                                        </TableCell>
                                        <TableCell>
                                            {result.syntheticCalls.toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {props.canGenerate && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={2}
                        sx={noteSx}
                    >
                        <InfoRounded
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            flex={1}
                        >
                            Changed the module, question or rubric? Clear this
                            set and generate a new one so synthetic calls match
                            the current setup.
                        </Typography>
                        <Box flexShrink={0}>
                            <GenerateSyntheticDataButton
                                label="Clear and start over"
                                color="error"
                            />
                        </Box>
                    </Stack>
                )}
            </Stack>
        );
    },
);

function SummaryCell(props: React.PropsWithChildren<{ label: string }>) {
    return (
        <Stack flex={1} rowGap={0.5} paddingX={2} paddingY={1}>
            <Typography variant="body2" color="text.secondary">
                {props.label}
            </Typography>
            {props.children}
        </Stack>
    );
}

const NoSyntheticSetYet = observer(() => {
    const store = useStore(LLMClassifierBuilderStore);
    const status = store.dataStepLabelCountsStatus;
    const humanLabels = getHumanLabelTotal(store);
    const hasNoLabels = status === "loaded" && humanLabels === 0;

    let description: React.ReactNode;
    if (hasNoLabels) {
        description =
            "No calls in the test set are labeled for this question. Widen the Test set filters, such as the date range, to include labeled calls.";
    } else {
        const labelCount =
            status === "loaded" ? (
                humanLabels.toLocaleString()
            ) : status === "loading" ? (
                <Skeleton
                    variant="text"
                    width="2.5em"
                    sx={{ display: "inline-block" }}
                />
            ) : null;
        description = (
            <>
                Write synthetic calls from the{" "}
                {labelCount && <>{labelCount} </>}
                {humanLabels === 1 ? "label" : "labels"} in{" "}
                {store.dataLabelingModule?.name} to add volume or fill in rare
                outcomes. Calls held for final testing are never used.
            </>
        );
    }

    return (
        <SyntheticDataNotice
            title="No synthetic set yet"
            description={description}
            action={
                <GenerateSyntheticDataButton label="Generate synthetic data" />
            }
        />
    );
});

// Only opened once the counts have loaded for the current question and filters.
const GenerateSyntheticDataDialog = observer(
    (props: { onClose: () => void }) => {
        const store = useStore(LLMClassifierBuilderStore);
        const messageStore = useStore(MessageStore);
        // New ids each time the dialog opens, so a double click can't start a batch twice.
        const [batchIds] = useState(() => new Map<string, string>());
        const [sentResults, setSentResults] = useState<Set<string>>(
            () => new Set(),
        );
        // A model that already has a set gets it replaced, failed results included.
        const [replacesCurrentSet] = useState(
            () => !!store.syntheticDataBatches?.length,
        );
        const [hasCleared, setHasCleared] = useState(false);
        // Counted when the dialog opens, since the list changes to the new set while it stays open.
        const [currentSetCalls] = useState(() =>
            getSavedCallTotal(store.syntheticDataBatches ?? []),
        );
        const isSubmitting =
            store.getTaskLoading(
                LLMClassifierBuilderStore.Tasks.RequestSyntheticDataBatches,
            ) ||
            store.getTaskLoading(
                LLMClassifierBuilderStore.Tasks.ClearSyntheticDataBatches,
            );

        const rows = getSyntheticDataRows(store);
        const question = store.dataLabelingQuestion;
        const plan = question
            ? planSyntheticDataBatches({
                  questionId: question.id,
                  labelName: getLabelName(question),
                  rows,
                  rubric: store.dataStepRubric,
                  labelRubricVersion: String(
                      store.classifier.implementation.versionNumber ?? "",
                  ),
                  batchIdFor: (result) => {
                      if (!batchIds.has(result)) batchIds.set(result, v4());
                      return batchIds.get(result)!;
                  },
              })
            : undefined;
        // Batches are linked to the model, which needs to exist on the server first.
        const isMissingBalanceTotal =
            store.dataStepGenerateMode === "perOutcome" &&
            store.dataStepBalanceToTotal &&
            store.dataStepBalanceTarget === undefined;
        // A total at or below every result's human labels adds nothing.
        const fewestHumanLabels = Math.min(
            ...rows
                .filter((row) => row.humanLabels > 0)
                .map((row) => row.humanLabels),
        );
        const isBalanceTotalTooLow =
            store.dataStepGenerateMode === "perOutcome" &&
            store.dataStepBalanceToTotal &&
            isFinite(fewestHumanLabels) &&
            rows.every((row) => row.syntheticCalls === 0);
        let blockedReason: string | undefined;
        if (!store.isSaved) {
            blockedReason = "Save the model before generating synthetic data.";
        } else if (isMissingBalanceTotal) {
            blockedReason = "Enter a total to balance each outcome to.";
        } else if (isBalanceTotalTooLow) {
            blockedReason = `Every result already has ${fewestHumanLabels.toLocaleString()} or more human labels, so balance to more than ${fewestHumanLabels.toLocaleString()} to add synthetic calls.`;
        } else if (plan && !plan.canGenerate) {
            blockedReason = plan.reason;
        } else if (store.hasUnsavedRubricChanges) {
            blockedReason =
                "Save the model to keep your rubric changes before generating synthetic data.";
        } else if (
            store.classifier.implementation.rubric?.questionId !== question?.id
        ) {
            blockedReason =
                "Save the model with a rubric for this question before generating synthetic data.";
        }
        // Batches record the saved rubric they're written from.
        const rubricId = store.classifier.implementation.savedRubricId;

        async function generate() {
            if (!plan?.canGenerate || blockedReason || !rubricId) return;
            // Clear only once, so a retry can't clear the batches that already started.
            if (replacesCurrentSet && !hasCleared) {
                if (!(await store.clearSyntheticDataBatches())) {
                    messageStore.logError(
                        "Couldn't clear the current synthetic data. Try again.",
                    );
                    return;
                }
                setHasCleared(true);
            }
            // After a partial failure, only the batches that didn't start are sent again.
            const requests = plan.requests.filter(
                (request) => !sentResults.has(request.labelValue),
            );
            const failed = await store.requestSyntheticDataBatches(
                requests,
                rubricId,
                plan.tagIds,
            );
            const nowSent = new Set(sentResults);
            for (const request of requests) {
                if (!failed.includes(request)) nowSent.add(request.labelValue);
            }
            setSentResults(nowSent);

            if (failed.length > 0) {
                messageStore.logError(
                    `Couldn't start synthetic data for ${failed
                        .map((request) => request.labelValue)
                        .join(", ")}. Try again.`,
                );
                return;
            }
            const calls = plan.requests.reduce(
                (sum, request) => sum + request.count,
                0,
            );
            messageStore.logMessage(
                `Started generating ${calls.toLocaleString()} synthetic ${
                    calls === 1 ? "call" : "calls"
                }.`,
                "success",
            );
            props.onClose();
        }

        const isPerOutcome = store.dataStepGenerateMode === "perOutcome";
        return (
            <AcxDialog
                isOpen
                onClose={props.onClose}
                title="Generate synthetic data"
                maxWidth="md"
                fullWidth
                buttonsDisabled={isSubmitting}
                dialogContentChildren={
                    <Stack rowGap={2}>
                        {replacesCurrentSet && currentSetCalls > 0 && (
                            <Alert severity="info">
                                Generating clears the current set of{" "}
                                {currentSetCalls.toLocaleString()} synthetic{" "}
                                {currentSetCalls === 1 ? "call" : "calls"} and
                                replaces it with this one.
                            </Alert>
                        )}
                        <Stack rowGap={1}>
                            <Typography color="text.secondary">
                                How much to generate
                            </Typography>
                            <Box width="50%">
                                <AcxToggleButton
                                    id="synthetic-generate-mode"
                                    exclusive
                                    defaultValue={store.dataStepGenerateMode}
                                    items={[
                                        {
                                            value: "callsPerLabel",
                                            element: "Calls per label",
                                        },
                                        {
                                            value: "perOutcome",
                                            element: "Choose per outcome",
                                        },
                                    ]}
                                    onChange={(value) => {
                                        // Clicking the selected tab sends null; keep it selected.
                                        if (value !== null)
                                            store.setDataStepGenerateMode(
                                                value,
                                            );
                                    }}
                                />
                            </Box>
                        </Stack>
                        {isPerOutcome ? (
                            <BalanceToTotalControl rows={rows} />
                        ) : (
                            <CallsPerLabelControl />
                        )}
                        <SyntheticDataSummary rows={rows} />
                        <SyntheticDataTable
                            rows={rows}
                            onSyntheticCallsChange={
                                isPerOutcome
                                    ? (result, syntheticCalls) =>
                                          store.setDataStepSyntheticCalls(
                                              result,
                                              syntheticCalls,
                                              rows,
                                          )
                                    : undefined
                            }
                        />
                        {getHighUpsamplingRows(rows).map((row) => (
                            <Alert key={row.result} severity="warning">
                                {row.result} has only{" "}
                                {row.humanLabels.toLocaleString()} seed{" "}
                                {row.humanLabels === 1 ? "label" : "labels"}.
                                Its {row.syntheticCalls.toLocaleString()}{" "}
                                synthetic calls will repeat patterns from a
                                small set, so check {row.result} results
                                closely.
                            </Alert>
                        ))}
                        {blockedReason && (
                            <Typography color="error">
                                {blockedReason}
                            </Typography>
                        )}
                    </Stack>
                }
            >
                <AcxButton
                    color="primary"
                    loading={isSubmitting}
                    disabled={
                        !plan?.canGenerate || !!blockedReason || isSubmitting
                    }
                    onClick={() => void generate()}
                >
                    Generate
                </AcxButton>
            </AcxDialog>
        );
    },
);

function getSyntheticDataRows(store: LLMClassifierBuilderStore) {
    const counts = store.dataStepLabelCounts ?? [];
    if (store.dataStepGenerateMode === "callsPerLabel")
        return getCallsPerLabelRows(
            counts,
            store.dataStepSyntheticCallsPerLabel,
        );
    return getPerOutcomeRows(counts, {
        // A blank total balances to nothing, so every result gets 0.
        balanceTo: store.dataStepBalanceToTotal
            ? store.dataStepBalanceTarget ?? 0
            : undefined,
        syntheticCallsByResult: store.dataStepSyntheticCallsByResult,
    });
}

/** Reads a number field, treating an empty or negative entry as 0. */
function toWholeNumber(event: React.ChangeEvent<HTMLInputElement>) {
    const value = Math.floor(event.target.valueAsNumber);
    return isNaN(value) || value < 0 ? 0 : value;
}

const CallsPerLabelControl = observer(() => {
    const store = useStore(LLMClassifierBuilderStore);
    const callsPerLabel = store.dataStepSyntheticCallsPerLabel;

    return (
        <Stack direction="row" alignItems="center" columnGap={2}>
            <Typography
                component="label"
                htmlFor="synthetic-calls-per-label"
                fontWeight={600}
            >
                Synthetic calls per label
            </Typography>
            <Box width="200px">
                <AcxSelectSingle
                    id="synthetic-calls-per-label"
                    fullWidth
                    isSearchable={false}
                    options={callsPerLabelOptions}
                    valueField="value"
                    labelField="label"
                    defaultValue={callsPerLabelOptions.find(
                        (option) => option.value === callsPerLabel,
                    )}
                    onChange={(option) =>
                        store.setDataStepSyntheticCallsPerLabel(option.value)
                    }
                />
            </Box>
        </Stack>
    );
});

const BalanceToTotalControl = observer(
    (props: { rows: SyntheticDataRow[] }) => {
        const store = useStore(LLMClassifierBuilderStore);

        return (
            <Stack direction="row" alignItems="center" columnGap={1}>
                <Checkbox
                    id="synthetic-balance-to-total"
                    checked={store.dataStepBalanceToTotal}
                    onChange={(event) =>
                        store.setDataStepBalanceToTotal(
                            event.target.checked,
                            props.rows,
                        )
                    }
                    sx={{ padding: 0 }}
                />
                <Typography
                    component="label"
                    htmlFor="synthetic-balance-to-total"
                >
                    Balance to
                </Typography>
                <Box width="140px">
                    <AcxMainTextField
                        id="synthetic-balance-target"
                        type="number"
                        fullWidth
                        isDisabled={!store.dataStepBalanceToTotal}
                        value={store.dataStepBalanceTarget ?? ""}
                        onChange={(event) =>
                            store.setDataStepBalanceTarget(
                                event.target.value === ""
                                    ? undefined
                                    : toWholeNumber(event),
                            )
                        }
                    />
                </Box>
                <Typography>total per outcome</Typography>
            </Stack>
        );
    },
);

const SyntheticDataSummary = observer((props: { rows: SyntheticDataRow[] }) => {
    const store = useStore(LLMClassifierBuilderStore);
    const humanLabels = props.rows.reduce(
        (sum, row) => sum + row.humanLabels,
        0,
    );
    const syntheticCalls = props.rows.reduce(
        (sum, row) => sum + row.syntheticCalls,
        0,
    );

    let description: string;
    if (store.dataStepGenerateMode === "callsPerLabel") {
        description = `${humanLabels.toLocaleString()} ${
            humanLabels === 1 ? "label" : "labels"
        } × ${store.dataStepSyntheticCallsPerLabel}`;
    } else if (store.dataStepBalanceToTotal) {
        description =
            store.dataStepBalanceTarget === undefined
                ? "Enter a total per outcome"
                : `Balanced to ${store.dataStepBalanceTarget.toLocaleString()} per outcome`;
    } else {
        description = "Chosen per outcome";
    }

    return (
        <Stack
            direction="row"
            alignItems="center"
            columnGap={2}
            sx={(theme) => ({
                backgroundColor: theme.palette.lightBlue.background,
                borderRadius: "8px",
                paddingX: 3,
                paddingY: 2,
            })}
        >
            <Typography color="text.secondary">{description}</Typography>
            <Typography color="text.secondary">=</Typography>
            <Typography
                fontWeight={600}
                sx={(theme) => ({ color: theme.palette.blue.main })}
            >
                {syntheticCalls.toLocaleString()} synthetic{" "}
                {syntheticCalls === 1 ? "call" : "calls"}
            </Typography>
            {syntheticCalls > 0 && (
                <Typography color="text.secondary" marginLeft="auto">
                    {getTokenEstimateText(syntheticCalls)}
                </Typography>
            )}
        </Stack>
    );
});

const noteSx: SxProps<Theme> = (theme) => ({
    border: `1px solid ${theme.palette.lightgrayBorder.main}`,
    borderRadius: "8px",
    backgroundColor: theme.palette.lightgray.background,
    paddingX: 2,
    paddingY: 1.5,
});

const tableContainerSx: SxProps<Theme> = (theme) => ({
    border: `1px solid ${theme.palette.lightgrayBorder.main}`,
    borderRadius: "8px",
});

const tableHeadSx: SxProps<Theme> = (theme) => ({
    backgroundColor: theme.palette.lightgray.background,
    "& .MuiTableCell-root": {
        color: theme.palette.text.secondary,
        fontSize: "12px",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
    },
});

/** Pass `onSyntheticCallsChange` to make the synthetic calls editable. */
function SyntheticDataTable(props: {
    rows: SyntheticDataRow[];
    onSyntheticCallsChange?: (result: string, syntheticCalls: number) => void;
}) {
    return (
        <TableContainer sx={tableContainerSx}>
            <Table>
                <TableHead sx={tableHeadSx}>
                    <TableRow>
                        <TableCell>Result</TableCell>
                        <TableCell>Human labels</TableCell>
                        <TableCell>Synthetic calls</TableCell>
                        <TableCell>Total</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody sx={{ "& tr:last-child td": { borderBottom: 0 } }}>
                    {props.rows.map((row) => (
                        <TableRow key={row.result}>
                            <TableCell sx={{ fontWeight: 600 }}>
                                {row.result}
                            </TableCell>
                            <TableCell>
                                {row.humanLabels.toLocaleString()}
                            </TableCell>
                            <TableCell>
                                {props.onSyntheticCallsChange ? (
                                    <Box width="120px">
                                        <AcxMainTextField
                                            id={`synthetic-calls-${row.result}`}
                                            type="number"
                                            fullWidth
                                            // A result with no labels has nothing to write from.
                                            isDisabled={row.humanLabels === 0}
                                            value={row.syntheticCalls}
                                            onChange={(event) =>
                                                props.onSyntheticCallsChange?.(
                                                    row.result,
                                                    toWholeNumber(event),
                                                )
                                            }
                                        />
                                    </Box>
                                ) : (
                                    row.syntheticCalls.toLocaleString()
                                )}
                            </TableCell>
                            <TableCell>{row.total.toLocaleString()}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function NeedsLabeledData() {
    return (
        <SyntheticDataNotice
            title="Needs labeled data"
            description="Synthetic calls are written from labeled calls and keep their labels. Link a module question on the Details step to generate a synthetic set, or skip this step and test without labels later."
        />
    );
}

function SyntheticDataNotice(props: {
    title: string;
    description: React.ReactNode;
    action?: React.ReactNode;
}) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            columnGap={3}
            sx={(theme) => ({
                border: `1px dashed ${theme.palette.lightgrayBorder.main}`,
                borderRadius: "8px",
                backgroundColor: theme.palette.lightgray.background,
                paddingX: 4,
                paddingY: 3,
            })}
        >
            <GridViewRounded sx={{ color: "text.disabled" }} />
            <Stack rowGap={0.5} flex={1}>
                <Typography fontWeight={600} color="text.secondary">
                    {props.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {props.description}
                </Typography>
            </Stack>
            {props.action && <Box flexShrink={0}>{props.action}</Box>}
        </Stack>
    );
}
